/**
 * THE BRIDGE (I1): store and bus → map engine. Created once the controller is ready.
 *
 *   const bridge = createBridge(ctrl, { introMode });   // in an effect keyed on ctrl
 *   bridge.choreo · bridge.settled · bridge.home() · bridge.showExtraPins(id) · bridge.destroy()
 *
 * What goes where (so nothing is driven twice):
 *   store.selectedId / bloomCategory / hoveredId / phase "drive"  → bindChoreographer (B4, src/map/camera/bind.ts)
 *   store.mood / layers / layout / insets                         → the controller follows the store itself (B2)
 *   store.group / category / layers.labels                        → the markers follow the store themselves (B5)
 *   bus map:click-place  → actions.select(id, "map")      (explore only)
 *   bus map:hover-place  → actions.hover(id)              (explore only, never in kiosk)
 *   bus map:click-empty  → actions.select(null, "map")    (explore only, when a place is open)
 *   dem                  → the controller adds the DEM source on "dem:ready" (B1/B2)
 *
 * Opening camera. A full intro starts at the region view at night (§E.7 beat 0). The bridge puts the map
 * there straight away, while the loader still covers it, so the tiles for that view are already drawn
 * when the visitor enters. `settled` resolves once the map has drawn a complete frame there (MapLibre
 * "idle"), or after SETTLE_CAP_MS; the loader offers "Open the map" only then (`stageReady`).
 *
 * StrictMode: createBridge/destroy are symmetric and leak-free (the effect may run twice).
 */
import type { LngLat } from "@/contracts/data";
import type { MapController } from "@/contracts/map";
import { bindChoreographer } from "@/map/camera/bind";
import { createChoreographer, type ChoreographerHandle } from "@/map/camera/choreo";
import { fitBBox, type ControllerHandle } from "@/map/controller";
import { on } from "@/store/bus";
import { actions, getApp } from "@/store/store";
import type { IntroMode } from "./flags";

export interface Bridge {
  readonly choreo: ChoreographerHandle;
  /** The map has drawn a complete frame at its opening camera (or the cap passed). */
  readonly settled: Promise<void>;
  /** "Back to the site" and the logo: close the card, the bloom and the chip, then choreo.home(). */
  home(): void;
  /** §C.9: fit a place's extra pins (easeTo 900 drift) and pulse each one. */
  showExtraPins(placeId: string): void;
  destroy(): void;
}

/** Longest the loader waits for the map's first complete frame before offering Enter anyway. */
export const SETTLE_CAP_MS = 5000;
/** Extra-pins fit: room around the pins inside the card-open frame (CSS px). */
const EXTRA_PAD = 56;
const EXTRA_MAX_ZOOM = 14.5;

/* ── the stage is ready (one per page): the loader gates "Open the map" on it ───────────────── */

let resolveStage: (() => void) | null = null;
/** Resolves once, the first time a bridge's map has settled at its opening camera. */
export const stageReady: Promise<void> = new Promise<void>((r) => {
  resolveStage = r;
});

/** Which phases take map clicks and hovers: the chrome is up and nothing else owns the map. */
export function mapInputLive(phase: string): boolean {
  return phase === "explore";
}

/** Bounding box of a set of points, padded to at least `minDeg` on each axis. */
export function bboxOf(points: readonly LngLat[], minDeg = 0.004): [number, number, number, number] {
  let x0 = Infinity;
  let y0 = Infinity;
  let x1 = -Infinity;
  let y1 = -Infinity;
  for (const [x, y] of points) {
    if (x < x0) x0 = x;
    if (x > x1) x1 = x;
    if (y < y0) y0 = y;
    if (y > y1) y1 = y;
  }
  const cx = (x0 + x1) / 2;
  const cy = (y0 + y1) / 2;
  const hx = Math.max((x1 - x0) / 2, minDeg / 2);
  const hy = Math.max((y1 - y0) / 2, minDeg / 2);
  return [cx - hx, cy - hy, cx + hx, cy + hy];
}

export function createBridge(ctrl: MapController, o: { introMode: IntroMode }): Bridge {
  const handle = ctrl as ControllerHandle;
  const choreo = createChoreographer(ctrl, { getState: getApp }) as ChoreographerHandle;
  const unbind = bindChoreographer(choreo);
  let alive = true;

  /* ── map input → store ── */
  const offs = [
    on("map:click-place", ({ id }) => {
      const s = getApp();
      if (!mapInputLive(s.phase)) return;
      if (s.selectedId !== id) actions.select(id, "map");
    }),
    on("map:hover-place", ({ id }) => {
      const s = getApp();
      if (s.kiosk || (id !== null && !mapInputLive(s.phase))) return;
      actions.hover(id);
    }),
    on("map:click-empty", () => {
      const s = getApp();
      if (mapInputLive(s.phase) && s.selectedId) actions.select(null, "map");
    }),
  ];

  /* ── opening camera, under the loader ── */
  if (o.introMode === "full") {
    try {
      ctrl.setMood("night", { duration: 0 });
      ctrl.jumpTo(ctrl.regionTarget());
      void handle.fadeContours?.(0, { duration: 0 });
    } catch (e) {
      console.warn("[bridge] opening camera", e);
    }
  }

  const settled = new Promise<void>((resolve) => {
    const map = ctrl.map();
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      clearTimeout(cap);
      map.off("idle", finish);
      resolve();
    };
    const cap = setTimeout(finish, SETTLE_CAP_MS);
    map.on("idle", finish);
    // Idle may already have fired before we listened, with nothing left to load.
    if (map.loaded() && map.areTilesLoaded() && !map.isMoving()) requestAnimationFrame(finish);
  });
  void settled.then(() => {
    if (alive) resolveStage?.();
  });

  return {
    choreo,
    settled,

    home(): void {
      if (!alive) return;
      const s = getApp();
      if (s.phase !== "explore" && s.phase !== "attract") return;
      if (s.bloomCategory) actions.bloom(null);
      if (s.category) actions.setCategory(null);
      if (s.selectedId) actions.select(null, "key");
      void choreo.home();
    },

    showExtraPins(placeId: string): void {
      if (!alive) return;
      const s = getApp();
      const p = s.data?.places.find((x) => x.id === placeId);
      if (!p || !p.extraPins.length) return;
      const pins = p.extraPins.map((e) => e.coord as LngLat);
      const map = ctrl.map();
      const box = map.getContainer().getBoundingClientRect();
      const i = ctrl.getInsets();
      const w = box.width - i.left - i.right;
      const h = box.height - i.top - i.bottom;
      const fit = fitBBox(bboxOf([p.coord as LngLat, ...pins]), w, h, EXTRA_PAD);
      const zoom = Math.min(EXTRA_MAX_ZOOM, fit.zoom);
      void ctrl.easeTo({ center: fit.center, zoom, bearing: 0, pitch: 0 }, { duration: 900, ease: "drift" }).then((r) => {
        if (!alive || r === "cancelled") return;
        try {
          const mk = ctrl.markers();
          pins.forEach((c) => mk.pulse(c, { count: 2 }));
        } catch {
          /* map gone */
        }
      });
    },

    destroy(): void {
      if (!alive) return;
      alive = false;
      offs.forEach((off) => off());
      unbind();
      choreo.destroy();
    },
  };
}
